// @flow

import * as React from 'react';
import {defineMessages, FormattedMessage} from 'react-intl';
import {actionsRolesAllow} from './ActionsRolesAllow';
import type {RoleDescriptionProps} from './RoleDescription';

const analystRoleMessages = defineMessages({
  roleName: {
    id: 'docsApp.components.contrib.roleDescriptions.analystRole.roleName',
    description: 'The name of the analyst role.',
    defaultMessage: 'Analyst',
  },
  shortDescription: {
    id:
      'docsApp.components.contrib.roleDescriptions.analystRole.shortDescription',
    description: 'The short description of the analyst role.',
    defaultMessage: 'Can pay out money, refund payments, and export data',
  },
  descriptionOfWhatRoleCanDo: {
    id:
      'docsApp.components.contrib.roleDescriptions.analystRole.descriptionOfWhatRoleCanDo',
    description: 'The description of what the analyst role can do.',
    defaultMessage:
      'This role is for people who need to pay out money, refund payments, and export data.',
  },
  descriptionOfWhatRoleCannotDo: {
    id:
      'docsApp.components.contrib.roleDescriptions.analystRole.descriptionOfWhatRoleCannotDo',
    description: "The description of what the analyst role can't do.",
    defaultMessage:
      "They can't edit connected accounts, payout schedules, or account settings.",
  },
});

const analystRoleDescriptionProps: RoleDescriptionProps = {
  roleName: <FormattedMessage {...analystRoleMessages.roleName} />,
  roleId: 'analyst',
  isBeta: false,
  shortDescription: (
    <FormattedMessage {...analystRoleMessages.shortDescription} />
  ),
  descriptionOfWhatRoleCanDo: (
    <FormattedMessage {...analystRoleMessages.descriptionOfWhatRoleCanDo} />
  ),
  descriptionOfWhatRoleCannotDo: (
    <FormattedMessage {...analystRoleMessages.descriptionOfWhatRoleCannotDo} />
  ),
  someThingsThisRoleCanDo: [
    <FormattedMessage {...actionsRolesAllow.viewAndResolveDisputes} />,
    <FormattedMessage {...actionsRolesAllow.createViewRefundPayments} />,
    <FormattedMessage {...actionsRolesAllow.createViewEditDeleteProducts} />,
    <FormattedMessage {...actionsRolesAllow.createViewEditDeleteCustomers} />,
    <FormattedMessage {...actionsRolesAllow.viewConnectedAccounts} />,
    <FormattedMessage {...actionsRolesAllow.viewBalance} />,
    <FormattedMessage {...actionsRolesAllow.viewTransfers} />,
    <FormattedMessage
      {...actionsRolesAllow.payoutBalanceToExternalBankAccount}
    />,
    <FormattedMessage {...actionsRolesAllow.viewRadar} />,
    <FormattedMessage {...actionsRolesAllow.viewEventsAndLogs} />,
    <FormattedMessage {...actionsRolesAllow.manageFinancialReports} />,
    <FormattedMessage {...actionsRolesAllow.bulkExport} />,
  ],
  someThingsThisRoleCannotDo: [
    <FormattedMessage
      {...actionsRolesAllow.createEditRejectConnectedAccounts}
    />,
    <FormattedMessage
      {...actionsRolesAllow.editConnectedAccountPayoutSchedule}
    />,
    <FormattedMessage
      {...actionsRolesAllow.transferBalanceAndReverseTransfers}
    />,
    <FormattedMessage {...actionsRolesAllow.editPayoutSchedule} />,
    <FormattedMessage {...actionsRolesAllow.addAndEditBankAccountDetails} />,
    <FormattedMessage {...actionsRolesAllow.createEditDeleteAPIKeys} />,
    <FormattedMessage {...actionsRolesAllow.addEditRemoveTeamMembers} />,
    <FormattedMessage {...actionsRolesAllow.editAccountDetails} />,
    <FormattedMessage {...actionsRolesAllow.changeAccountOwner} />,
    <FormattedMessage {...actionsRolesAllow.configureAnyProductSettings} />,
    <FormattedMessage
      {...actionsRolesAllow.connectAccountToConnectPlatforms}
      values={{
        a: (content) => <a href="https://stripe.com/docs/connect">{content}</a>,
      }}
    />,
  ],
};

const disputeAnalystRoleMessages = defineMessages({
  roleName: {
    id:
      'docsApp.components.contrib.roleDescriptions.disputeAnalystRole.roleName',
    description: 'The name of the dispute analyst role.',
    defaultMessage: 'Dispute Analyst',
  },
  shortDescription: {
    id:
      'docsApp.components.contrib.roleDescriptions.disputeAnalystRole.shortDescription',
    description: 'The short description of the dispute analyst role.',
    defaultMessage: 'Can view and resolve disputes',
  },
  descriptionOfWhatRoleCanDo: {
    id:
      'docsApp.components.contrib.roleDescriptions.disputeAnalystRole.descriptionOfWhatRoleCanDo',
    description: 'The description of what the dispute analyst role can do.',
    defaultMessage:
      'This role is for people who need need to view, submit evidence for, and accept disputes.',
  },
  descriptionOfWhatRoleCannotDo: {
    id:
      'docsApp.components.contrib.roleDescriptions.disputeAnalystRole.descriptionOfWhatRoleCannotDo',
    description: "The description of what the dispute analyst role can't do.",
    defaultMessage: "They can't do anything that's not related to disputes.",
  },
});

const disputeAnalystRoleDescriptionProps: RoleDescriptionProps = {
  roleName: <FormattedMessage {...disputeAnalystRoleMessages.roleName} />,
  roleId: 'dispute_analyst',
  isBeta: true,
  shortDescription: (
    <FormattedMessage {...disputeAnalystRoleMessages.shortDescription} />
  ),
  descriptionOfWhatRoleCanDo: (
    <FormattedMessage
      {...disputeAnalystRoleMessages.descriptionOfWhatRoleCanDo}
    />
  ),
  descriptionOfWhatRoleCannotDo: (
    <FormattedMessage
      {...disputeAnalystRoleMessages.descriptionOfWhatRoleCannotDo}
    />
  ),
  someThingsThisRoleCanDo: [
    <FormattedMessage {...actionsRolesAllow.viewAndResolveDisputes} />,
    <FormattedMessage {...actionsRolesAllow.viewPayments} />,
    <FormattedMessage {...actionsRolesAllow.viewProducts} />,
    <FormattedMessage {...actionsRolesAllow.viewCustomers} />,
  ],
  someThingsThisRoleCannotDo: [
    <FormattedMessage {...actionsRolesAllow.createViewRefundPayments} />,
    <FormattedMessage {...actionsRolesAllow.createViewEditDeleteProducts} />,
    <FormattedMessage {...actionsRolesAllow.createViewEditDeleteCustomers} />,
    <FormattedMessage
      {...actionsRolesAllow.createViewEditRejectConnectedAccounts}
    />,
    <FormattedMessage {...actionsRolesAllow.editConnectedAcctCapabilities} />,
    <FormattedMessage
      {...actionsRolesAllow.editConnectedAccountPayoutSchedule}
    />,
    <FormattedMessage {...actionsRolesAllow.viewBalance} />,
    <FormattedMessage {...actionsRolesAllow.viewTransfers} />,
    <FormattedMessage
      {...actionsRolesAllow.transferBalanceAndReverseTransfers}
    />,
    <FormattedMessage
      {...actionsRolesAllow.payoutBalanceToExternalBankAccount}
    />,
    <FormattedMessage {...actionsRolesAllow.editPayoutSchedule} />,
    <FormattedMessage {...actionsRolesAllow.addAndEditBankAccountDetails} />,
    <FormattedMessage {...actionsRolesAllow.createEditDeleteAPIKeys} />,
    <FormattedMessage {...actionsRolesAllow.viewEventsAndLogs} />,
    <FormattedMessage {...actionsRolesAllow.addEditRemoveTeamMembers} />,
    <FormattedMessage {...actionsRolesAllow.editAccountDetails} />,
    <FormattedMessage {...actionsRolesAllow.changeAccountOwner} />,
    <FormattedMessage {...actionsRolesAllow.viewRadar} />,
    <FormattedMessage {...actionsRolesAllow.configureAnyProductSettings} />,
    <FormattedMessage
      {...actionsRolesAllow.connectAccountToConnectPlatforms}
      values={{
        a: (content) => <a href="https://stripe.com/docs/connect">{content}</a>,
      }}
    />,
    <FormattedMessage {...actionsRolesAllow.manageFinancialReports} />,
    <FormattedMessage {...actionsRolesAllow.bulkExport} />,
  ],
};

export const allPaymentRoleProps = [
  analystRoleDescriptionProps,
  disputeAnalystRoleDescriptionProps,
];
